// Opportunity anlegen oder bearbeiten (Betrag mit Einmalig/Monatlich/Jährlich, Status, Abschlussdatum, Konfidenz, Zuständig,
// Kontakt, Notiz). Wird von der Pipeline und der Liste benutzt und lässt sich von der Lead-Seite aus einsetzen:
//   <OpportunityDialog offen onSchliessen={…} lead={{ id, name }} onGespeichert={…} />
// Gespeichert wird nach Close mit dem Schlüssel des Handelnden (API.md 20.5); der Dialog zeigt die Hinweise des Servers.
import { useEffect, useMemo, useState } from "react";
import { leadsApi, type Kontakt, type Opportunity } from "../api";
import { opportunitiesApi, type OpportunityAenderung, type OpportunityZeile } from "../api/opportunities";
import { useNutzer } from "../auth/AuthContext";
import { Auswahl, type Option } from "../components/Auswahl";
import { Dialog } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { useMeta } from "../daten/meta";
import { oppFehlerText } from "./fehlertext";
import { LeadWahl } from "./LeadWahl";
import { PERIODEN, centAusText, centZuText, statusFarbe, type Periode } from "./logik";
import "./opportunities.css";

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  /** Bearbeiten: die Opportunity (auch die schlanke Form der Lead-Seite, `lead_name` darf fehlen); ohne sie wird eine neue angelegt */
  opportunity?: (Opportunity & { lead_name?: string | null }) | null;
  /** neu: Lead vorbelegt (z. B. von der Lead-Seite); ohne ihn wählt man ihn im Dialog */
  lead?: { id: string; name: string | null } | null;
  /** neu: Status vorbelegt (z. B. die Spalte, in der man „+“ gedrückt hat) */
  statusId?: string | null;
  onGespeichert?: (opp: OpportunityZeile, neu: boolean) => void;
}

const CLOSING_LABEL = "closing call terminiert";

export function OpportunityDialog({ offen, onSchliessen, opportunity, lead, statusId, onGespeichert }: Props) {
  const ich = useNutzer();
  const { meta, nutzerListe } = useMeta();
  const meldung = useMeldung();
  const bearbeiten = !!opportunity;
  const pipelines = meta?.pipelines ?? [];

  const standardStatus = useMemo(() => {
    if (statusId) return statusId;
    for (const p of pipelines) {
      const s = p.statuses.find((x) => x.type === "active");
      if (s) return s.id;
    }
    return null;
  }, [pipelines, statusId]);

  const [leadWahl, setLeadWahl] = useState<{ id: string; name: string | null } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [betrag, setBetrag] = useState("");
  const [periode, setPeriode] = useState<Periode>("one_time");
  const [datum, setDatum] = useState("");
  const [konfidenz, setKonfidenz] = useState(50);
  const [zustaendig, setZustaendig] = useState<string | null>(null);
  const [kontakt, setKontakt] = useState<string | null>(null);
  const [notiz, setNotiz] = useState("");
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [schonAktiv, setSchonAktiv] = useState<OpportunityZeile[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [leadFehlt, setLeadFehlt] = useState(false);
  const [betragFehler, setBetragFehler] = useState(false);

  // Beim Öffnen die Felder setzen
  useEffect(() => {
    if (!offen) return;
    setFehler(null);
    setLeadFehlt(false);
    setBetragFehler(false);
    setSpeichert(false);
    if (opportunity) {
      setLeadWahl({ id: opportunity.lead_id, name: opportunity.lead_name ?? null });
      setStatus(opportunity.status_id);
      setBetrag(centZuText(opportunity.value));
      setPeriode((opportunity.value_period as Periode | null) ?? "one_time");
      setDatum(opportunity.date_won ?? "");
      setKonfidenz(opportunity.confidence ?? 0);
      setZustaendig(opportunity.user_id);
      setKontakt(opportunity.contact_id);
      setNotiz(opportunity.note ?? "");
    } else {
      setLeadWahl(lead ?? null);
      setStatus(standardStatus);
      setBetrag("");
      setPeriode("one_time");
      setDatum("");
      setKonfidenz(50);
      setZustaendig(ich.close_user_id ?? ich.id);
      setKontakt(null);
      setNotiz("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, opportunity?.id]);

  // Kontakte des Leads und bestehende aktive Opportunities (Hinweis) holen
  const leadId = leadWahl?.id ?? null;
  useEffect(() => {
    if (!offen || !leadId) {
      setKontakte([]);
      setSchonAktiv([]);
      return;
    }
    let weg = false;
    void leadsApi
      .detail(leadId)
      .then((d) => {
        if (!weg) setKontakte(d.contacts);
      })
      .catch(() => {
        if (!weg) setKontakte([]);
      });
    if (!bearbeiten) {
      void opportunitiesApi
        .liste({ lead_id: leadId, status_type: "active", limit: 5 })
        .then((r) => {
          if (!weg) setSchonAktiv(r.data);
        })
        .catch(() => {
          if (!weg) setSchonAktiv([]);
        });
    }
    return () => {
      weg = true;
    };
  }, [offen, leadId, bearbeiten]);

  const statusOptionen: Option[] = pipelines.flatMap((p) =>
    p.statuses.map((s) => ({ wert: s.id, label: pipelines.length > 1 ? `${p.name} · ${s.label}` : s.label, farbe: statusFarbe(s.type) })),
  );
  const nutzerOptionen: Option[] = nutzerListe.filter((u) => u.is_active || u.close_user_id === opportunity?.user_id).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name }));
  const kontaktOptionen: Option[] = kontakte.map((k) => ({ wert: k.id, label: k.display_name ?? k.name ?? "Kontakt", unter: k.title }));
  const gewaehlterStatus = pipelines.flatMap((p) => p.statuses).find((s) => s.id === status);
  const fremd = bearbeiten && opportunity!.user_id !== (ich.close_user_id ?? ich.id) && !ich.permissions.includes("manage_others_opportunities");

  const speichern = async () => {
    if (speichert) return;
    setFehler(null);
    if (!leadId) {
      setLeadFehlt(true);
      setFehler("Bitte einen Lead wählen.");
      return;
    }
    if (!status) {
      setFehler("Bitte einen Status wählen.");
      return;
    }
    const cent = centAusText(betrag);
    if (Number.isNaN(cent)) {
      setBetragFehler(true);
      setFehler("Der Betrag ist nicht lesbar. Beispiel: 1.200 oder 1200,50.");
      return;
    }
    setBetragFehler(false);
    setSpeichert(true);
    try {
      const gemeinsam = {
        status_id: status,
        value: cent,
        value_period: cent ? periode : undefined,
        confidence: konfidenz,
        date_won: datum || null,
        user_id: zustaendig ?? undefined,
        contact_id: kontakt,
        note: notiz.trim() || null,
      };
      const r = bearbeiten
        ? await opportunitiesApi.aendern(opportunity!.id, gemeinsam as OpportunityAenderung)
        : await opportunitiesApi.anlegen({
            lead_id: leadId,
            status_id: status,
            value: cent ?? undefined,
            value_period: cent ? periode : undefined,
            confidence: konfidenz,
            date_won: datum || undefined,
            user_id: zustaendig ?? undefined,
            contact_id: kontakt ?? undefined,
            note: notiz.trim() || undefined,
          });
      meldung.ok(bearbeiten ? "Opportunity gespeichert." : "Opportunity angelegt.");
      for (const h of r.hinweise ?? []) meldung.zeige("info", h, undefined, 9000);
      onGespeichert?.(r.opportunity, !bearbeiten);
      onSchliessen();
    } catch (e) {
      setSpeichert(false);
      setFehler(oppFehlerText(e));
    }
  };

  const zeigeClosingHinweis = !!gewaehlterStatus && gewaehlterStatus.label.trim().toLowerCase() === CLOSING_LABEL;

  return (
    <Dialog
      titel={bearbeiten ? "Opportunity bearbeiten" : "Neue Opportunity"}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={480}
      fuss={
        <>
          <button type="button" className="knopf" onClick={onSchliessen} disabled={speichert}>
            Abbrechen
          </button>
          <button type="button" className="knopf knopf--primaer" onClick={() => void speichern()} disabled={speichert || fremd} title={fremd ? "Fremde Opportunity: dafür fehlt die Berechtigung." : "Speichern (Strg/⌘ + Enter)"}>
            {speichert ? "Speichert …" : "Speichern"}
          </button>
        </>
      }
    >
      <form
        className="opp-formular"
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void speichern();
          }
        }}
      >
        <div className="feld">
          <span className="feld__label">Lead</span>
          {bearbeiten ? (
            <div className="opp-leadwahl__gewaehlt">
              <Icon name="leads" groesse={14} />
              <span className="opp-leadwahl__name">{leadWahl?.name ?? opportunity?.lead_id}</span>
            </div>
          ) : (
            <LeadWahl leadId={leadId} leadName={leadWahl?.name ?? null} onWahl={(l) => { setLeadWahl(l); setLeadFehlt(false); }} fehler={leadFehlt} />
          )}
        </div>
        {schonAktiv.length ? (
          <div className="hinweis hinweis--warnung" role="status">
            <Icon name="warnung" groesse={14} />
            <span>
              Dieser Lead hat schon eine aktive Opportunity ({[...new Set(schonAktiv.map((o) => o.status_label ?? "aktiv"))].join(", ")}). Das Backoffice stellt beim nächsten Closing Call nur eine davon um, zuerst die mit „Closing Call Terminiert“.
            </span>
          </div>
        ) : null}
        <div className="opp-formular__zeile opp-formular__zeile--betrag">
          <label className="feld">
            <span className="feld__label">Betrag (€)</span>
            <input
              className={`eingabe${betragFehler ? " eingabe--fehler" : ""}`}
              inputMode="decimal"
              placeholder="0"
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
              aria-invalid={betragFehler}
            />
          </label>
          <label className="feld">
            <span className="feld__label">Zahlung</span>
            <select className="eingabe" value={periode} onChange={(e) => setPeriode(e.target.value as Periode)}>
              {PERIODEN.map((p) => (
                <option key={p.wert} value={p.wert}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="feld">
          <span className="feld__label">Status</span>
          <Auswahl optionen={statusOptionen} wert={status} onWahl={(w) => setStatus(w as string | null)} platzhalter="Status wählen …" pflicht />
        </div>
        <div className="opp-formular__zeile">
          <label className="feld">
            <span className="feld__label">Abschlussdatum</span>
            <input type="date" className="eingabe" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="feld">
            <span className="feld__label">
              Konfidenz <b className="opp-formular__prozent">{konfidenz} %</b>
            </span>
            <input type="range" min={0} max={100} step={5} className="opp-formular__regler" value={konfidenz} onChange={(e) => setKonfidenz(Number(e.target.value))} aria-label="Konfidenz in Prozent" />
          </label>
        </div>
        {zeigeClosingHinweis ? (
          <p className="opp-formular__hinweis">Beim nächsten Closing Call setzt das Backoffice Abschlussdatum und Notiz dieser Opportunity neu.</p>
        ) : null}
        <div className="opp-formular__zeile">
          <div className="feld">
            <span className="feld__label">Zuständig</span>
            <Auswahl optionen={nutzerOptionen} wert={zustaendig} onWahl={(w) => setZustaendig(w as string | null)} platzhalter="Niemand" />
          </div>
          <div className="feld">
            <span className="feld__label">Kontakt</span>
            <Auswahl optionen={kontaktOptionen} wert={kontakt} onWahl={(w) => setKontakt(w as string | null)} leerErlaubt={!bearbeiten || !opportunity?.contact_id} platzhalter={leadId ? "Kein Kontakt" : "Erst Lead wählen"} deaktiviert={!leadId} />
          </div>
        </div>
        <label className="feld">
          <span className="feld__label">Notiz</span>
          <textarea className="eingabe eingabe--text" rows={3} value={notiz} placeholder="Notiz hinzufügen …" onChange={(e) => setNotiz(e.target.value)} />
        </label>
        {fehler ? (
          <div className="hinweis hinweis--fehler" role="alert">
            <Icon name="warnung" groesse={14} />
            <span>{fehler}</span>
          </div>
        ) : null}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}
